import { calcComponentTotal } from "../hooks/useCalculations"
import { roundNumber, quantityOf } from "./formatting"

// Options stay out of the totals above them, as on the PDF; a lot of only options is one.
export const isOption = node => {
  if (node?.type !== "group") return false
  if (node.option) return true
  const priced = (node.children || []).filter(c => !c._destroy && c.type !== "descriptive")
  return priced.length > 0 && priced.every(isOption)
}

// Each line is rounded to the cent (not once at the end) so the builder subtotal
// matches the persisted/index/PDF value, which sums per-line-rounded amounts.
export const nodeTotals = node => {
  if (!node) return { total: 0, cost: 0, unitSale: 0, unitCost: 0 }
  let nodeIsOption = null
  const skipped = child => {
    if (child.type !== "group") return false
    if (nodeIsOption === null) nodeIsOption = isOption(node)
    return !nodeIsOption && isOption(child)
  }

  if (node.type === "service") {
    const sale = calcComponentTotal(node, "sale")
    const cost = calcComponentTotal(node, "cost")
    return { total: sale, cost, unitSale: sale, unitCost: cost }
  }

  const liveChildren    = (node.children || []).filter(c => !c._destroy)
  const hasLineChildren = liveChildren.some(c => c.type === "service")

  if (hasLineChildren) {
    const factor     = quantityOf(node.quantity)
    let unitSale = 0
    let unitCost = 0
    // The node's quantity multiplies its own lines; a sub-lot carries its own.
    let heldSale = 0
    let heldCost = 0
    liveChildren.forEach(child => {
      if (child.type !== "service") {
        if (skipped(child)) return
        const held = nodeTotals(child)
        heldSale += held.total
        heldCost += held.cost
        return
      }
      unitSale += calcComponentTotal(child, "sale")
      unitCost += calcComponentTotal(child, "cost")
    })
    return {
      total: roundNumber(factor * unitSale * discountFactor(node) + heldSale),
      cost:  roundNumber(factor * unitCost + heldCost),
      unitSale,
      unitCost,
    }
  }

  let total = 0
  let cost  = 0
  liveChildren.forEach(child => {
    if (skipped(child)) return
    const t = nodeTotals(child)
    total += t.total
    cost  += t.cost
  })
  return { total: roundNumber(total), cost: roundNumber(cost), unitSale: roundNumber(total), unitCost: roundNumber(cost) }
}

export const optionsTotal = node => {
  if (!node || node._destroy || node.type !== "group") return 0
  if (isOption(node)) return nodeTotals(node).total
  return roundNumber((node.children || []).reduce((sum, child) => sum + optionsTotal(child), 0))
}

// A work's own discount (qline.discount, %) applies to its lines, not to the sub-lots it holds.
export const discountFactor = node => 1 - (parseFloat(node?.discount) || 0) / 100

export const collectVatBreakdown = (node, acc = {}, parentQty = 1) => {
  if (!node || node._destroy || isOption(node)) return acc

  const liveChildren = (node.children || []).filter(c => c && !c._destroy)
  const services     = liveChildren.filter(c => c.type === "service")

  if (services.length > 0) {
    const pct = node.vat_rate?.percentage
    if (pct != null) {
      const qty  = quantityOf(node.quantity)
      const sale = services.reduce((s, c) => s + calcComponentTotal(c, "sale", qty), 0) * parentQty * discountFactor(node)
      acc[pct] = (acc[pct] || 0) + sale
    }
    liveChildren.filter(c => c.type !== "service")
                .forEach(child => collectVatBreakdown(child, acc, parentQty))
    return acc
  }

  const childQty = quantityOf(node.quantity)
  liveChildren.forEach(child => collectVatBreakdown(child, acc, parentQty * childQty))
  return acc
}

export const hasLiveChildren = node => (node?.children || []).some(child => !child._destroy)

export const isBreakdown = node => node?.type === "service" || node?.type === "descriptive"

export const holdsLots = node => (node?.children || []).some(child => child.type === "group" && !child._destroy)

// A lot holding sub-lots holds no line: one added there goes to its last sub-lot.
export const lineHolderUid = (sections, uid) => {
  const find = nodes => {
    for (const node of nodes || []) {
      if (node._uid === uid) return node
      const hit = find(node.children)
      if (hit) return hit
    }
    return null
  }
  let node = find(sections)
  while (node && holdsLots(node)) {
    const lots = node.children.filter(child => child.type === "group" && !child._destroy)
    node = lots[lots.length - 1]
  }
  return node?._uid || null
}

const settledByUid = new WeakMap()

const settledChildren = (node, settled) => {
  if (!settled) return null
  let byUid = settledByUid.get(settled)
  if (!byUid) {
    byUid = new Map()
    settled.forEach(candidate => {
      if (candidate && !byUid.has(candidate._uid)) byUid.set(candidate._uid, candidate)
    })
    settledByUid.set(settled, byUid)
  }
  return byUid.get(node._uid)?.children || null
}

// A lot holding sub-lots keeps its descriptions before them, where the PDF prints them.
// `settled` is the same list before the edit: a node found unchanged in it is skipped with its subtree.
export const descriptionsFirst = (nodes, settled = null) => {
  if (nodes === settled) return nodes
  let changed = false
  const out = (nodes || []).map((node, index) => {
    if (node === settled?.[index]) return node
    if (node.type !== "group" || !node.children || node.children.length === 0) return node

    const children = descriptionsFirst(node.children, settledChildren(node, settled))
    let next = children === node.children ? node : { ...node, children }

    if (children.some(child => child.type === "group")) {
      const ordered = [
        ...children.filter(child => child.type === "descriptive"),
        ...children.filter(child => child.type !== "descriptive"),
      ]
      if (ordered.some((child, index) => child !== children[index])) next = { ...next, children: ordered }
    }

    if (next !== node) changed = true
    return next
  })
  return changed ? out : nodes
}

export const rollUpComposedLines = (nodes, settled = null) => {
  if (nodes === settled) return nodes
  let changed = false
  const out = (nodes || []).map((node, index) => {
    if (node === settled?.[index]) return node
    if (!node.children || node.children.length === 0) return node

    const children = rollUpComposedLines(node.children, settledChildren(node, settled))
    let next = children === node.children ? node : { ...node, children }

    if (node.type === "service" && hasLiveChildren(next)) {
      const live           = children.filter(child => !child._destroy)
      const unit_price     = roundNumber(live.reduce((sum, child) => sum + calcComponentTotal(child, "sale"), 0))
      const purchase_price = roundNumber(live.reduce((sum, child) => sum + calcComponentTotal(child, "cost"), 0))
      if (unit_price !== parseFloat(node.unit_price) || purchase_price !== parseFloat(node.purchase_price)) {
        next = { ...next, unit_price, purchase_price }
      }
    }

    if (next !== node) changed = true
    return next
  })
  return changed ? out : nodes
}

export const countBy = (node, type) => {
  let n = 0
  const walk = x => {
    if (x._destroy) return
    if (x.type === type) n++
    ;(x.children || []).forEach(walk)
    ;(x.components || []).forEach(walk)
  }
  ;(node.children || []).forEach(walk)
  ;(node.components || []).forEach(walk)
  return n
}

export const directChildrenCount = node => {
  if (!node) return 0
  if (node.type === "group") return (node.components || []).filter(c => !c._destroy).length
  return (node.children || []).filter(c => !c._destroy).length
}

const COST_CATEGORY_LABEL = {
  labor:          () => I18n.t("quotes.new.cost_category.labor"),
  equipment:      () => I18n.t("quotes.new.cost_category.equipment"),
  materials:      () => I18n.t("quotes.new.cost_category.materials"),
  subcontracting: () => I18n.t("quotes.new.cost_category.subcontracting"),
  other:          () => I18n.t("quotes.new.cost_category.other"),
}

export const collectByCostCategory = node => {
  const acc = {}
  const walk = (n, factor, saleFactor = 1) => {
    if (!n || n._destroy || isOption(n)) return
    if (n.type === "service" && hasLiveChildren(n)) {
      n.children.forEach(c => walk(c, factor * (parseFloat(n.quantity) || 0), saleFactor))
      return
    }
    if (n.type === "service") {
      const qty           = (parseFloat(n.quantity) || 0) * factor
      const sale          = qty * (parseFloat(n.unit_price)     || 0) * saleFactor
      const cost          = qty * (parseFloat(n.purchase_price) || 0)
      const cost_category = n.cost_category || "materials"
      const labelFn       = COST_CATEGORY_LABEL[cost_category]
      const entry         = (acc[cost_category] ||= {
        sale:     0,
        cost:     0,
        hours:    0,
        isLabour: cost_category === "labor",
        name:     labelFn ? labelFn() : cost_category,
      })
      entry.sale += sale
      entry.cost += cost
      if (entry.isLabour) entry.hours += qty
      return
    }
    const liveChildren    = (n.children || []).filter(c => !c._destroy)
    const hasServiceChild = liveChildren.some(c => c.type === "service")
    const serviceFactor   = hasServiceChild ? factor * quantityOf(n.quantity) : factor
    liveChildren.forEach(c => c.type === "service" ? walk(c, serviceFactor, discountFactor(n)) : walk(c, factor))
  }
  walk(node, 1)
  return acc
}
